import { memo, useCallback, useEffect } from 'react';
import {
  useSharedValue,
  withSequence,
  withTiming,
} from 'react-native-reanimated';
import { apiConfig } from '@bonanza-social/toolkit/api';
import { AsyncStorage } from '@bonanza-social/toolkit/storage';
import { StackNavigationProp, useNavigation } from '@bonanza-social/navigation';
import { useDispatch, useSelector } from '@bonanza-social/toolkit/state';
import { Box, Button } from '@bonanza-social/moonlight';
import {
  selectPrizeWallState,
  selectPrizeWallQuickRules,
} from '../../state/prizeWall/prizeWall.slice';
import {
  setQuickRulesShown,
  selectPrizeWallCampaignId,
} from '../../state/prizeWallCampaign/prizeWallCampaign.slice';
import { PrizeWallCampaignNavigatorParamsList } from '../../navigators/PrizeWallCampaignNavigator/PrizeWallCampaignNavigator.types';
import { PrizeWallQuickRulesProps } from './PrizeWallQuickRules.types';
import PrizeWallQuickRule from './PrizeWallQuickRule/PrizeWallQuickRule';

function PrizeWallQuickRules({
  shouldAnimate = true,
  shouldGoBack = true,
  ...rest
}: PrizeWallQuickRulesProps) {
  const navigation =
    useNavigation<StackNavigationProp<PrizeWallCampaignNavigatorParamsList>>();
  const dispatch = useDispatch();
  const state = useSelector(selectPrizeWallState);
  const rules = useSelector(selectPrizeWallQuickRules);
  const campaignId = useSelector(selectPrizeWallCampaignId);
  const animatedValue = useSharedValue(-1);
  const hasRules = !!rules;

  const onButtonPress = useCallback(() => {
    if (shouldGoBack) {
      navigation.goBack();

      return;
    }

    navigation.navigate(
      state === 'Finished' ? 'PrizeWallWinnersScreen' : 'PrizeWallScreen',
    );
  }, [state, shouldGoBack, navigation]);

  useEffect(() => {
    if (shouldAnimate && hasRules) {
      animatedValue.value = withSequence(
        ...Array.from({ length: 3 }, (_, index) =>
          withTiming(index, { duration: 1000 }),
        ),
      );
    }
  }, [shouldAnimate, animatedValue, hasRules]);

  useEffect(() => {
    if (campaignId) {
      const markRulesAsShown = async () => {
        const userId = await apiConfig.getUserId();
        const quickRulesShownMapping = await AsyncStorage.getItem(
          'PRIZE_WALL_QUICK_RULES_SHOWN_MAPPING',
        );
        const parsedMapping = quickRulesShownMapping
          ? JSON.parse(quickRulesShownMapping)
          : {};

        parsedMapping[campaignId] = [
          ...(parsedMapping[campaignId] || []),
          userId,
        ];

        AsyncStorage.setItem(
          'PRIZE_WALL_QUICK_RULES_SHOWN_MAPPING',
          JSON.stringify(parsedMapping),
        );

        dispatch(setQuickRulesShown(true));
      };

      markRulesAsShown();
    }
  }, [campaignId, dispatch]);

  return (
    <Box flex={1} {...rest}>
      <Box flex={1} centerContent>
        {rules?.map((rule, index) => (
          <PrizeWallQuickRule
            key={index}
            index={index}
            arrow={
              index === rules.length - 1
                ? undefined
                : index % 2 === 0
                  ? 'quickRuleArrowRight'
                  : 'quickRuleArrowLeft'
            }
            animatedValue={animatedValue}
            {...rule}
          />
        ))}
      </Box>
      <Button
        locNS="common"
        locKey={shouldGoBack ? 'goBack' : 'continue'}
        marginHorizontal="space-600"
        onPress={onButtonPress}
      />
    </Box>
  );
}

export default memo(PrizeWallQuickRules);
